import { Component, Vue, Prop } from 'vue-property-decorator';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import { NOTIFICATION, PRIZE_TYPES, REWARD_REQUEST_STATUSES } from '@/constants';
import { RewardItemDisplayDTO, RewardRequest, RewardRequestDTO, UserDTO } from '@/models';
import { createRewardRequest } from '@/services';
import { Debounce } from '@/decorators';

const DEFAULT_TYPE_LIMIT = 3;

@Component({
  components: {
    Tooltip
  }
})
export default class PrizeCard extends Vue {
  @Prop({ default: {} as RewardItemDisplayDTO }) data: RewardItemDisplayDTO;
  @Prop({ default: false }) isAddPrize: boolean;
  @Prop() notEnoughDrops: boolean;
  @Prop() hasShippingInfo: boolean;
  @Prop({ default: 0 }) rewardPeriodId: number;
  @Prop({ default: [] as RewardRequestDTO[] }) exchangeRewardsHistory: RewardRequestDTO[];
  @Prop({ default: () => ({}) }) rewardPeriodSettings: Record<string, number>;
  private imageUrl = process.env.VUE_APP_HIVE_HOME_URL + '/images/';
  private displayConfirmButton = false;
  private currentUser: UserDTO = {} as UserDTO;

  onChangeDisplayConfirmButton(): void {
    this.displayConfirmButton = !this.displayConfirmButton;
  }

  getImageSource(imgStr: string): string {
    if (this.isAddPrize) {
      return imgStr;
    }
    return this.imageUrl + imgStr;
  }

  get leftQuantity(): number {
    return this.data.quantity - this.data.redeemCount;
  }

  get isDisabled(): boolean {
    return this.leftQuantity <= 0 || this.notEnoughDrops || this.isRedeemLimitReached;
  }

  get isRedeemLimitReached(): boolean {
    const isItemLimitReached = !!this.data.maxRedeemPerUser && (this.data.userRedeemCount || 0) >= this.data.maxRedeemPerUser;
    const isTypeLimitReached = this.typeRedeemLimit !== null && this.typeRedeemedInPeriod >= this.typeRedeemLimit;
    return isItemLimitReached || isTypeLimitReached;
  }

  get redeemLimitTooltip(): string {
    if (this.typeRedeemLimit !== null && this.typeRedeemedInPeriod >= this.typeRedeemLimit) {
      const formattedType = this.data.type.toLowerCase().replace(/_/g, ' ');
      return `You have reached the maximum number of ${formattedType} redemptions for this reward period`;
    }
    if (this.data.maxRedeemPerUser && (this.data.userRedeemCount || 0) >= this.data.maxRedeemPerUser) {
      return 'You have reached the maximum redemption limit for this item';
    }
    return '';
  }

  onNotifyToCreateShippingInfo(): void {
    const dataNotification = {
      showing: true,
      type: '',
      title: '',
      content: [],
      icon: '',
      multiline: false,
      height: 0
    };
    dataNotification.type = NOTIFICATION.TYPE.error;
    dataNotification.title = NOTIFICATION.TEXT.mustAddShippingInfo;
    dataNotification.icon = NOTIFICATION.ICON.error;
    this.$store.commit('notification/setNotification', dataNotification);

    this.$emit('onShippingInfoDialogOpen');
  }

  get typeRedeemedInPeriod(): number {
    return this.exchangeRewardsHistory.filter(
      request =>
        request.rewardItem.prize.type === this.data.type &&
        request.periodRewardId === this.rewardPeriodId &&
        request.status !== REWARD_REQUEST_STATUSES.REJECTED.label
    ).length;
  }

  get typeRedeemLimit(): number | null {
    return (this.rewardPeriodSettings || {})[this.data.type] ?? null;
  }

  @Debounce(50)
  async onConfirmRedeemPrize(): Promise<void> {
    const formattedType = this.data.type.toLowerCase().replace(/_/g, ' ');
    if (
      this.typeRedeemLimit !== null &&
      this.typeRedeemedInPeriod >= this.typeRedeemLimit
    ) {
      const dataNotification = {
        showing: true,
        type: NOTIFICATION.TYPE.error,
        title: NOTIFICATION.TEXT.voucherRedeemLimitReached.replace('{formattedType}', formattedType),
        content: [],
        icon: NOTIFICATION.ICON.error,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', dataNotification);
      this.onChangeDisplayConfirmButton();
      return;
    }
    const dataRewardRequest: RewardRequest = {
      userId: this.currentUser.id,
      rewardItemId: this.data.id,
      status: REWARD_REQUEST_STATUSES.PENDING.label
    };
    let createRewardRequestResult: any;
    try {
      createRewardRequestResult = await createRewardRequest(dataRewardRequest);
    } catch (error: any) {
      createRewardRequestResult = error;
    }
    const dataNotification = {
      showing: true,
      type: '',
      title: '',
      content: [],
      icon: '',
      multiline: false,
      height: 0
    };
    if (createRewardRequestResult.data) {
      dataNotification.type = NOTIFICATION.TYPE.success;
      dataNotification.title = NOTIFICATION.TEXT.confirmRedeemPrizeSuccess;
      dataNotification.icon = NOTIFICATION.ICON.success;

      this.$emit('onRefetchData', true);
      this.$emit('showConfetti');
    } else {
      dataNotification.type = NOTIFICATION.TYPE.error;
      dataNotification.title =
        createRewardRequestResult.response?.data?.message || NOTIFICATION.TEXT.confirmRedeemPrizeFailed;
      dataNotification.icon = NOTIFICATION.ICON.error;
    }
    this.$store.commit('notification/setNotification', dataNotification);

    if (createRewardRequestResult.data) {
      const hasTypeLimit = this.typeRedeemLimit !== null;
      const hasItemLimit = this.data.maxRedeemPerUser !== null && this.data.maxRedeemPerUser !== undefined;

      if (hasTypeLimit || hasItemLimit) {
        const typeLimitRemaining = hasTypeLimit ? this.typeRedeemLimit! - 1 - this.typeRedeemedInPeriod : Infinity;
        const itemLimitRemaining = hasItemLimit ? this.data.maxRedeemPerUser! - (this.data.userRedeemCount || 0) - 1 : Infinity;
        const stockRemaining = this.leftQuantity - 1;
        
        const remaining = Math.min(typeLimitRemaining, itemLimitRemaining, stockRemaining);

        if (remaining > 0) {
          const infoNotification = {
            showing: true,
            type: NOTIFICATION.TYPE.info,
            title: `You can select up to ${remaining} more ${formattedType}${remaining > 1 ? 's' : ''} for your gift basket.`,
            content: [],
            icon: NOTIFICATION.ICON.info,
            multiline: false,
            height: 0,
            position: 'bottom'
          };
          this.$store.commit('notification/setNotification', infoNotification);
        } else {
          const infoNotification = {
            showing: true,
            type: NOTIFICATION.TYPE.info,
            title: `You have reached the maximum number of ${formattedType} allowed in your gift basket.`,
            content: [],
            icon: NOTIFICATION.ICON.info,
            multiline: false,
            height: 0,
            position: 'bottom'
          };
          this.$store.commit('notification/setNotification', infoNotification);
        }
      }
    }

    this.onChangeDisplayConfirmButton();
  }

  created(): void {
    this.currentUser = this.$store.getters['app/currentUser'];
  }
}
